/**
 * The black scanner card: `N OF M IN` in cyan, the rainbow-ringed square the
 * camera fades into, the result flash, the hint, and whatever buttons the host
 * surface puts under it. `scanner` is the return of `useDoorScanner`.
 *
 * `size="tab"` is the Host Event DOOR tab's 220px frame; the full door page
 * lets it grow to 320px with the card.
 */
import React from 'react';
import { TONES } from './doorKinds';
import './door.css';

const SANS = { fontFamily: 'inherit' };

export function ScanBracket({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <path d="M3 8V3h5M16 3h5v5M21 16v5h-5M8 21H3v-5M7 12h10" />
    </svg>
  );
}

function FlipIcon() {
  // Lucide refresh-ccw substitute.
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5" />
    </svg>
  );
}

export function DoorFrame({ scanner, size = 'full' }) {
  const { live, active, facingUser, cameraCount, flash, camError, nativeRes, regionId } = scanner;
  const tone = flash ? TONES[flash.tone] : null;
  return (
    <div className="door-frame" data-size={size} data-testid="door-frame">
      <div className="door-frame-inner">
        {nativeRes ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={scanner.liveVideoRef}
            className="door-cam"
            data-live={live ? 'true' : 'false'}
            data-mirror={facingUser ? 'true' : 'false'}
            autoPlay
            muted
            playsInline
          />
        ) : (
          // html5-qrcode injects its <video> in here.
          <div id={regionId} className="door-cam" data-live={live ? 'true' : 'false'} data-mirror={facingUser ? 'true' : 'false'} />
        )}

        <div className="door-idle" style={{ opacity: active ? 0 : 1 }} aria-hidden="true">
          <ScanBracket />
          <span className="door-scanline" />
        </div>

        {live && (
          <div className="door-guides" aria-hidden="true"><span /><span /><span /><span /></div>
        )}

        {live && cameraCount >= 2 && (
          <button
            type="button"
            onClick={scanner.flip}
            aria-label="Flip camera"
            style={{
              ...SANS, position: 'absolute', top: 12, right: 12, height: 34, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6,
              border: 'none', borderRadius: 999, background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer',
            }}
          >
            <FlipIcon />FLIP
          </button>
        )}

        {flash && (
          <div
            key={flash.id}
            className="door-flash door-rise"
            role="status"
            aria-live="assertive"
            data-testid="door-flash"
            style={{ background: tone.bg, color: tone.ink }}
          >
            <span style={{ fontSize: size === 'tab' ? 22 : 26, fontWeight: 800, letterSpacing: '.12em' }}>{flash.title}</span>
            {flash.name && <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.06em' }}>{flash.name}</span>}
            {flash.sub && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', opacity: 0.75 }}>{flash.sub}</span>}
          </div>
        )}

        {camError && !flash && (
          <span style={{ position: 'absolute', left: 12, right: 12, bottom: 12, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: '#fff' }}>
            {camError.short}
          </span>
        )}
      </div>
    </div>
  );
}

export const btnWhite = {
  ...SANS, height: 52, width: '100%', border: 'none', borderRadius: 999, background: '#fff', color: '#000',
  fontSize: 12, fontWeight: 700, letterSpacing: '.16em', cursor: 'pointer',
};
export const btnDark = {
  ...SANS, height: 52, width: '100%', border: 'none', borderRadius: 999, background: '#1A1A1A', color: '#fff',
  fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer',
};

export default function DoorScannerCard({ scanner, counter, size = 'full', pad = '24px 20px', children }) {
  const { kind, camError } = scanner;
  return (
    <section
      className="door-root door-rise"
      data-testid="door-scanner"
      style={{
        background: '#000', color: '#fff', padding: pad, borderRadius: 28, display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 18, textAlign: 'center', minWidth: 0,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.2em', color: '#00FFFF' }} data-testid="door-line">{counter}</span>
      <DoorFrame scanner={scanner} size={size} />
      <span style={{ fontSize: 12, lineHeight: 1.6, color: '#D1D5DB', maxWidth: 380 }}>
        Point the camera at a guest’s {kind.noun} QR. Works offline; syncs when you’re back on signal.
      </span>
      {camError?.detail && (
        <span style={{ fontSize: 11, lineHeight: 1.5, color: '#FCA5A5', maxWidth: 380 }}>{camError.detail}</span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 420 }}>
        <button type="button" className="door-btn-white" style={btnWhite} onClick={scanner.toggle}>
          {scanner.active ? 'STOP SCANNING' : 'START SCANNING'}
        </button>
        {children}
        <input
          ref={scanner.photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={scanner.handlePhoto}
          data-testid="door-photo-input"
        />
      </div>
      {scanner.debugCam && (
        <>
          {scanner.live && (
            <button type="button" className="door-btn-dark" style={btnDark} onClick={scanner.grabFrame}>SCAN FRAME (VIDEO)</button>
          )}
          {scanner.camStats && (
            <p style={{ margin: 0, fontSize: 10, lineHeight: 1.6, color: '#9CA3AF', wordBreak: 'break-all' }}>
              {scanner.camStats.video} · frames {scanner.camStats.frames ?? '?'} · decoder {String(scanner.camStats.decoded)}
              {` · shown ${scanner.camStats.client} · ${scanner.camStats.state} · canvas ${scanner.camStats.canvas}`}
            </p>
          )}
        </>
      )}
    </section>
  );
}
